import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { I18n } from '@/lib/api/types';
import { FieldShell, inputBase } from '@/lib/ui/Field';
import { useCharLimit } from '@/lib/ui/CharCounter';
import { cn } from '@/lib/ui/cn';
import { useServerLocales } from '@/lib/i18n/useServerLocales';

/**
 * Paired EN/AR input (spec §6.1). Per-field RTL on the AR side regardless of chrome
 * language (spec §6.3). Completeness chip. In translate-only mode the EN side is read-only.
 *
 * The locales come from the server (`useServerLocales`, capabilities `brand.locales`), with
 * a bilingual fallback while that loads or if it fails — never English alone. On a
 * single-locale deployment the pair collapses to one plain English input: no EN/AR tab
 * strip, no completeness chip — an Arabic tab nobody will fill would be a permanently
 * amber "AR ·" on every string. Any Arabic already stored is carried through untouched on
 * every edit, so hiding the pane never deletes a translation.
 */
export function I18nField({
  label,
  value,
  onChange,
  required,
  error,
  multiline,
  translateOnly,
  disabled,
  hint,
  locales: localesProp,
  maxLength,
}: {
  label: string;
  value: I18n | undefined;
  onChange: (v: I18n) => void;
  required?: boolean;
  error?: string | null;
  multiline?: boolean;
  translateOnly?: boolean;
  disabled?: boolean;
  hint?: string;
  /**
   * Overrides the server's content locales (tests, or a caller that already holds them);
   * Arabic absent collapses the field to English.
   */
  locales?: readonly ('en' | 'ar')[];
  /** Per-locale `maxLength` from the schema, in graphemes: a hard stop and an "N left" counter. */
  maxLength?: number;
}) {
  const { t } = useTranslation();
  const server = useServerLocales();
  const locales = localesProp ?? server.locales;
  const resolvedLabel = t(label, { defaultValue: label });
  const v: I18n = { en: value?.en ?? '', ar: value?.ar ?? '' };
  const [tab, setTab] = useState<'en' | 'ar'>(translateOnly ? 'ar' : 'en');
  const enComplete = !!v.en.trim();
  const arComplete = !!v.ar.trim();

  const chip = (
    <span className="flex items-center gap-[6px] font-mono text-label-sm uppercase tracking-[0.1em]">
      <span className={enComplete ? 'text-signal-teal' : 'text-ink-mute'}>
        EN{enComplete ? ' ✓' : ' ·'}
      </span>
      <span className={arComplete ? 'text-signal-teal' : 'text-signal-amber-ink'}>
        AR{arComplete ? ' ✓' : ' ·'}
      </span>
    </span>
  );

  const Tag = multiline ? 'textarea' : 'input';
  // Translate-only is an Arabic-side workflow; it cannot exist without Arabic, so the pane
  // stays whenever a caller asks for it.
  const bilingual = locales.includes('ar') || !!translateOnly;
  const active = bilingual ? tab : 'en';
  // The counter follows the pane on screen; each locale is limited on its own.
  const limit = useCharLimit(active === 'en' ? v.en : v.ar, maxLength);
  const limitAttrs = limit
    ? {
        maxLength: limit.inputProps.maxLength,
        onPaste: limit.inputProps.onPaste,
        onBlur: limit.inputProps.onBlur,
        'aria-describedby': limit.describedBy,
      }
    : {};
  const arMissing = bilingual && !arComplete && (enComplete || !!required);

  // `data-ar-missing` is what the publish-blockers notice's "show the first missing Arabic
  // field" link looks for (in document order).
  return (
    <div data-i18n-field="" data-ar-missing={arMissing ? 'true' : undefined}>
      <FieldShell
        label={resolvedLabel}
        required={required}
        error={error}
        hint={hint}
        labelAdornment={bilingual ? chip : undefined}
      >
        {bilingual && (
          <div className="flex overflow-hidden rounded-sm border-[0.5px] border-hairline">
            <button
              type="button"
              onClick={() => setTab('en')}
              data-locale-tab="en"
              className={cn(
                'px-sm py-[6px] font-mono text-label uppercase tracking-[0.1em]',
                tab === 'en'
                  ? 'bg-accent-blue text-canvas-paper'
                  : 'bg-canvas-deep-sand text-ink-mute',
              )}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setTab('ar')}
              data-locale-tab="ar"
              className={cn(
                'px-sm py-[6px] font-mono text-label uppercase tracking-[0.1em]',
                tab === 'ar'
                  ? 'bg-accent-blue text-canvas-paper'
                  : 'bg-canvas-deep-sand text-ink-mute',
              )}
            >
              AR
            </button>
          </div>
        )}
        {active === 'en' ? (
          <Tag
            dir="ltr"
            lang="en"
            value={v.en}
            readOnly={translateOnly || disabled}
            onChange={(e: { target: { value: string } }) => onChange({ ...v, en: e.target.value })}
            className={cn(
              inputBase,
              multiline && 'min-h-[80px] resize-y',
              translateOnly && 'opacity-70',
            )}
            aria-label={t('forms.fieldEnglish', { label: resolvedLabel })}
            {...limitAttrs}
          />
        ) : (
          <Tag
            dir="rtl"
            lang="ar"
            value={v.ar}
            readOnly={disabled}
            onChange={(e: { target: { value: string } }) => onChange({ ...v, ar: e.target.value })}
            className={cn(inputBase, 'font-arabic', multiline && 'min-h-[80px] resize-y')}
            aria-label={t('forms.fieldArabic', { label: resolvedLabel })}
            {...limitAttrs}
          />
        )}
        {limit?.counter}
      </FieldShell>
    </div>
  );
}
